import { useState, useRef, useCallback, useEffect } from 'react';

export type RecordingState = 'idle' | 'recording' | 'processing';

// Number of frequency bands for visualization
const FREQUENCY_BANDS = 8;

export interface UseAudioRecordingReturn {
  recordingState: RecordingState;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<Blob | null>;
  audioBlob: Blob | null;
  error: string | null;
  isSupported: boolean;
  audioLevels: number[]; // Array of frequency band levels (0-1)
}

/**
 * Hook for recording audio using the MediaRecorder API
 *
 * Usage:
 * ```tsx
 * const { recordingState, startRecording, stopRecording, error } = useAudioRecording();
 *
 * // Start recording
 * await startRecording();
 *
 * // Stop and get audio blob
 * const blob = await stopRecording();
 * ```
 */
export function useAudioRecording(): UseAudioRecordingReturn {
  const [recordingState, setRecordingState] = useState<RecordingState>('idle');
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [audioLevels, setAudioLevels] = useState<number[]>(
    new Array(FREQUENCY_BANDS).fill(0)
  );

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  // Audio analysis refs
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Check if browser supports MediaRecorder
  const isSupported = typeof window !== 'undefined' &&
                      'MediaRecorder' in window &&
                      'mediaDevices' in navigator &&
                      'getUserMedia' in navigator.mediaDevices;

  /**
   * Start audio analysis for visualization
   */
  const startAudioAnalysis = useCallback((stream: MediaStream) => {
    try {
      // Create audio context
      const AudioContextClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!AudioContextClass) return;

      const audioContext = new AudioContextClass();
      audioContextRef.current = audioContext;

      // Create analyser node
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256; // Smaller FFT for better performance
      analyser.smoothingTimeConstant = 0.7; // Smooth transitions
      analyserRef.current = analyser;

      // Connect stream to analyser
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
      sourceRef.current = source;

      // Buffer for frequency data
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      // Animation loop to update audio levels
      const updateLevels = () => {
        if (!analyserRef.current) return;

        analyserRef.current.getByteFrequencyData(dataArray);

        // Calculate levels for each frequency band
        const bandSize = Math.floor(bufferLength / FREQUENCY_BANDS);
        const levels: number[] = [];

        for (let i = 0; i < FREQUENCY_BANDS; i++) {
          const start = i * bandSize;
          const end = start + bandSize;
          let sum = 0;

          for (let j = start; j < end; j++) {
            sum += dataArray[j];
          }

          // Normalize to 0-1 range with some boost for visibility
          const average = sum / bandSize;
          const normalized = Math.min(1, (average / 255) * 1.5);
          levels.push(normalized);
        }

        setAudioLevels(levels);
        animationFrameRef.current = requestAnimationFrame(updateLevels);
      };

      updateLevels();
    } catch (err) {
      console.warn('Audio analysis not available:', err);
    }
  }, []);

  /**
   * Stop audio analysis and clean up resources
   */
  const stopAudioAnalysis = useCallback(() => {
    // Cancel animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Disconnect source
    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }

    // Close audio context
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    analyserRef.current = null;

    // Reset levels to zero
    setAudioLevels(new Array(FREQUENCY_BANDS).fill(0));
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAudioAnalysis();
    };
  }, [stopAudioAnalysis]);

  /**
   * Start recording audio from the user's microphone
   */
  const startRecording = useCallback(async () => {
    if (!isSupported) {
      setError('Ihr Browser unterstützt keine Audioaufnahme');
      return;
    }

    try {
      setError(null);
      audioChunksRef.current = [];

      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        }
      });

      streamRef.current = stream;

      // Start audio analysis for visualization
      startAudioAnalysis(stream);

      // Determine the best MIME type for the browser
      let mimeType = 'audio/webm';
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
      } else if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
        mimeType = 'audio/ogg;codecs=opus';
      }

      // Create MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: 128000 // 128 kbps
      });

      mediaRecorderRef.current = mediaRecorder;

      // Collect audio data chunks
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      // Handle recording stop
      mediaRecorder.onstop = () => {
        // Create blob from chunks
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        setAudioBlob(blob);

        // Stop audio analysis
        stopAudioAnalysis();

        // Stop all tracks to release microphone
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }

        setRecordingState('idle');
      };

      // Handle errors
      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event);
        setError('Fehler bei der Aufnahme');
        setRecordingState('idle');

        // Stop audio analysis
        stopAudioAnalysis();

        // Clean up
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }
      };

      // Start recording
      mediaRecorder.start();
      setRecordingState('recording');

    } catch (err) {
      console.error('Failed to start recording:', err);

      // Handle specific errors
      if (err instanceof DOMException) {
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          setError('Mikrofon-Zugriff wurde verweigert. Bitte erlauben Sie den Zugriff in Ihren Browser-Einstellungen.');
        } else if (err.name === 'NotFoundError') {
          setError('Kein Mikrofon gefunden. Bitte schließen Sie ein Mikrofon an.');
        } else {
          setError('Fehler beim Zugriff auf das Mikrofon');
        }
      } else {
        setError('Fehler beim Starten der Aufnahme');
      }

      setRecordingState('idle');
    }
  }, [isSupported, startAudioAnalysis, stopAudioAnalysis]);

  /**
   * Stop recording and return the audio blob
   */
  const stopRecording = useCallback(async (): Promise<Blob | null> => {
    if (!mediaRecorderRef.current || recordingState !== 'recording') {
      return null;
    }

    return new Promise((resolve) => {
      const mediaRecorder = mediaRecorderRef.current;

      if (!mediaRecorder) {
        resolve(null);
        return;
      }

      // Override onstop to resolve the promise
      const originalOnStop = mediaRecorder.onstop;
      mediaRecorder.onstop = (event) => {
        // Call original handler
        if (originalOnStop) {
          originalOnStop.call(mediaRecorder, event);
        }

        // Create and resolve with blob
        const blob = new Blob(audioChunksRef.current, { type: mediaRecorder.mimeType });
        resolve(blob);
      };

      // Stop the recorder
      mediaRecorder.stop();
      setRecordingState('processing');
    });
  }, [recordingState]);

  return {
    recordingState,
    startRecording,
    stopRecording,
    audioBlob,
    error,
    isSupported,
    audioLevels
  };
}
